import React, { useState } from 'react'
import { useMoteCancelMutation, useTrackMutation } from '@mote/api'
import { PermissionDenied, getAudioStream, useFileContext } from '@mote/recipes'
import {
  MILLISECONDS_PER_CHUNK,
  TRACK_EVENT_NAMES,
  __TEST__
} from '../../constants'
import { useFlightRecorder } from '../../contexts'
import { DiagnosticsLogger } from '../../diagnostics'
import { useMicrophonePermission } from '../microphone'
import { useUnmountEffect } from '../useUnmountEffect'
import { AudioCache } from './AudioCache'
import { useMediaUploader } from './useMediaUploader'

export const RECORDER_STATUS = Object.freeze({
  inactive: 'inactive',
  recording: 'recording',
  paused: 'paused',
  stopped: 'stopped',
  cancelled: 'cancelled'
})

const defaultOptions = {
  parentMoteId: null,
  maxSeconds: 30,
  shouldRedirect: false,
  hasOptedOutOfTranscription: true,
  manualStatusHandling: false,
  getRegisterExtraPayload: () => ({})
}

const logD = new DiagnosticsLogger('useRecorder', 'Recorder')

export const useRecorder = (options = defaultOptions) => {
  const mergedOptions = { ...defaultOptions, ...options }
  const {
    streamingTranscriptData,
    registeredMote,
    register,
    sendChunk,
    finalize,
    pause: pauseRecording,
    finalizeData,
    cleanup: uploaderCleanup,
    error: uploaderError
  } = useMediaUploader({
    parentMoteId: mergedOptions.parentMoteId,
    shouldRedirect: mergedOptions.shouldRedirect,
    getRegisterExtraPayload: mergedOptions.getRegisterExtraPayload,
    hasOptedOutOfTranscription: mergedOptions.hasOptedOutOfTranscription
  })
  const micPermission = useMicrophonePermission()
  const trackMutation = useTrackMutation()
  const cancelMote = useMoteCancelMutation()
  const flightRecorder = useFlightRecorder()
  const fileContext = useFileContext()
  const [audioCache] = useState(() => new AudioCache())

  const [status, setStatus] = React.useState(RECORDER_STATUS.inactive)
  const [error, setError] = React.useState(null)

  const stream = React.useRef(null)
  const recorder = React.useRef(null)
  const chunkCount = React.useRef(null)

  const maxChunkCount =
    (mergedOptions.maxSeconds * 1000) / MILLISECONDS_PER_CHUNK

  function handleStatusChange(
    status,
    manualHandle = mergedOptions.manualStatusHandling
  ) {
    if (manualHandle) {
      return
    }

    setStatus(status)
  }

  async function getStream() {
    try {
      if (
        micPermission.permissionStatus === 'prompt' ||
        micPermission.permissionStatus === null
      ) {
        trackMutation.mutate({
          variables: {
            event: TRACK_EVENT_NAMES.permission.microphone.requestTriggered
          }
        })
        logD.info('Asked user for microphone permission')
      }

      const audioStream = await getAudioStream({
        audio: true
      })

      if (audioStream) {
        logD.info('Got audiostream from window.navigator')
        stream.current = audioStream
      } else {
        logD.error('Did not receive audiostream from window.navigator')
      }
    } catch (error) {
      console.error('Error getting stream', error)
      if (error instanceof PermissionDenied) {
        logD.warn('Microphone permission denied, asking user to fix')
        setError('FIX_MICROPHONE_PERMISSION')
        trackMutation.mutate({
          variables: {
            event: TRACK_EVENT_NAMES.permission.microphone.requestBlocked
          }
        })
        return
      }
      setError(error)
    }
  }

  function isStreamAvailable(stream) {
    if (__TEST__) {
      return true
    }

    const isMediaStream = stream instanceof MediaStream
    const isNotActive = isMediaStream && !stream.active

    if (!isMediaStream || isNotActive) {
      if (isNotActive) {
        logD.error('No stream available')
        setError('STREAM_UNAVAILABLE_ERROR')
      }
      return false
    }

    return true
  }

  async function start(onRegisterErrorCb) {
    flightRecorder.resetFlightState()
    cleanup()

    if (!stream.current) {
      await getStream()
    }

    if (!isStreamAvailable(stream.current)) {
      return null
    }

    register().catch(() => {
      onRegisterErrorCb?.()
    })

    const resolvedStream = stream.current

    recorder.current = new MediaRecorder(resolvedStream)
    recorder.current.addEventListener('dataavailable', handleChunk)
    recorder.current.addEventListener('stop', handleStop)
    recorder.current.start(MILLISECONDS_PER_CHUNK)
    handleStatusChange(RECORDER_STATUS.recording)

    logD.info('Recording started')
    return resolvedStream
  }

  function pause() {
    if (recorder.current?.state !== RECORDER_STATUS.recording) {
      return
    }

    recorder.current.pause()
    handleStatusChange(RECORDER_STATUS.paused)

    pauseRecording()

    logD.info('Recording paused')
  }

  function resume() {
    if (recorder.current?.state !== RECORDER_STATUS.paused) {
      return
    }

    recorder.current.resume()
    handleStatusChange(RECORDER_STATUS.recording)

    logD.info('Recording resumed')
  }

  async function handleStop() {
    if (!stream.current) {
      return
    }
    removeRecorderListeners()
    stream.current.getTracks().forEach(track => track.stop())
    stream.current = null
  }

  function resetStatus() {
    handleStatusChange(RECORDER_STATUS.inactive)
  }

  async function cancel(options) {
    if (!recorder.current) {
      return
    }

    recorder.current.stop()
    playSound(await fileContext.repository.getCancelRecordingSound())
    handleStatusChange(RECORDER_STATUS.cancelled)
    logD.info('Recording cancelled')

    handleCancelMote()

    cleanup(options)
  }

  function handleCancelMote(initialRegister = false) {
    if (!registeredMote) {
      return
    }

    const registerPayload = mergedOptions.getRegisterExtraPayload?.() || {}
    return cancelMote.mutate({
      variables: {
        moteId: registeredMote.moteStatus.moteId,
        lastChunkId: !initialRegister ? chunkCount.current : 0,
        isAnonymous: Boolean(registerPayload.isAnonymous)
      }
    })
  }

  function handleChunk(e) {
    if (!e.data || !e.data.size) {
      return
    }
    if (!recorder.current) {
      return
    }

    chunkCount.current++

    const hitChunkLimit = chunkCount.current >= maxChunkCount
    if (hitChunkLimit) {
      stop()
    } else {
      sendChunk(e.data)
    }
  }

  function cleanup(options = {}) {
    setError(null)

    if (options.status && RECORDER_STATUS[options.status]) {
      handleStatusChange(RECORDER_STATUS[options.status])
    }

    removeRecorderListeners()

    chunkCount.current = 0

    uploaderCleanup(options)
  }

  function removeRecorderListeners() {
    if (!recorder.current) {
      return
    }

    recorder.current.removeEventListener('dataavailable', handleChunk)
    recorder.current.removeEventListener('stop', handleStop)
    recorder.current = null
  }

  async function playSound(url) {
    await audioCache.get(url).play()
  }

  async function stop(additionalPayload = {}) {
    if (!recorder.current) {
      return
    }

    recorder.current.stop()
    playSound(await fileContext.repository.getFinishRecordingSound())
    handleStatusChange(RECORDER_STATUS.stopped)
    logD.info('Stopped recording')

    if (!registeredMote) {
      logD.info('Mote not registered yet')
      return false
    } else {
      if (flightRecorder.chunksInFlight) {
        return false
      } else {
        return await finalize(additionalPayload)
      }
    }
  }

  useUnmountEffect(() => {
    if (stream.current) {
      stream.current.getTracks().forEach(track => track.clone().stop())
    }
  }, [])

  return {
    getStream,
    start,
    pause,
    stop,
    cancel,
    resume,
    error,
    uploaderError,
    cleanup,
    finalizeRecording: finalize,
    finalizeData,
    resetStatus,
    registeredMote,
    streamingTranscriptData,
    handleStatusChange,
    status: {
      raw: status,
      isRecording: status === RECORDER_STATUS.recording,
      isPaused: status === RECORDER_STATUS.paused,
      isStopped: status === RECORDER_STATUS.stopped,
      isCancelled: status === RECORDER_STATUS.cancelled,
      isInactive: status === RECORDER_STATUS.inactive,
      isIdle: [RECORDER_STATUS.inactive, RECORDER_STATUS.cancelled].includes(
        status
      )
    }
  }
}
